/**
 * Module dependencies,
 */

import { Container } from 'src/components/core/layout/container';
import { GetServerSideProps, NextPage } from 'next';
import { Locale } from 'i18n-routes';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Service } from 'src/types/service';
import { ServicesList } from 'src/components/layout/services/list';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { getLocalizedCategoryByServicesSlug } from 'src/core/utils/routes';
import { getServices } from 'src/requests/service';
import { media } from 'src/styles/utils/media';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import styled, { createGlobalStyle } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: {
    services: Service[];
  };
};

/**
 * `GlobalStyle` styled component.
 */

const GlobalStyle = createGlobalStyle`
  body {
    background-color: var(--color-white);
  }
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  color: var(--color-gray700);
  padding-top: calc(var(--navbar-height) + 3rem);

  ${media.min.ms`
    padding-top: calc(var(--navbar-height) + 5rem);
  `}
`;

/**
 * `ServiceDetail` page.
 */

const ServiceDetail: NextPage<Props> = ({ data }) => {
  const { locale, query } = useRouter();
  const category = getLocalizedCategoryByServicesSlug(
    locale as Locale,
    query?.subCategory as string
  );

  const { t } = useTranslation();
  const subCategory = camelCase(category as string);
  const title = t(`common:service.subCategory.${camelCase(subCategory)}`);

  return (
    <>
      <SEO title={title} />

      <GlobalStyle />

      <TriggerNavbarTheme theme={'light'} />

      <Wrapper>
        <Container>
          <ServicesList
            category={data?.services?.[0]?.category}
            items={data?.services}
            title={title}
          />
        </Container>
      </Wrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps<
  SSGProps
> = async context => {
  try {
    const { locale, params } = context;
    const slug = params?.subCategory as string;
    const category = getLocalizedCategoryByServicesSlug(locale as Locale, slug);

    if (!slug || !category) {
      throw new Error('notFound');
    }

    const services = await getServices(locale as Locale, {
      filter: {
        sub_category: snakeCase(category) as Service['subCategory']
      }
    });

    if (isEmpty(services)) {
      throw new Error('notFound');
    }

    return {
      props: {
        data: { services },
        navbarTheme: 'light',
        ...(await serverSideTranslations(locale as Locale, [
          'common',
          'serviceDetail'
        ]))
      }
    };
  } catch (error) {
    return {
      notFound: true
    };
  }
};

/**
 * Export `ServiceDetail` component.
 */

export default ServiceDetail;
